import React, { useState, useEffect, type Dispatch } from 'react';
import type { RestaurantFormData } from '../restaurant.types';
import { FiMinus } from "react-icons/fi";
import { LuHandCoins } from "react-icons/lu";
import { IoFastFoodOutline } from "react-icons/io5";
import { FaWineBottle } from "react-icons/fa";
import { GiCrownedHeart } from "react-icons/gi";

interface BudgetStepProps {
  formData: RestaurantFormData;
  updateFormData: (data: Partial<RestaurantFormData>) => void;
  isActive: boolean;
}

const BudgetStep: React.FC<BudgetStepProps> = ({
  formData,
  updateFormData,
  isActive,
}) => {
  const [localPriceRange, setLocalPriceRange] = useState({
    min: formData.priceRange?.min || 1000,
    max: formData.priceRange?.max || 20000,
  });

  const budgetOptions = [
    {
      value: '$',
      label: 'Affordable',
      icon: <LuHandCoins />,
      priceRange: { min: 300, max: 1200 },
    },
    {
      value: '$$',
      label: 'Mid-range',
      icon: <IoFastFoodOutline />,
      priceRange: { min: 1200, max: 4800 },
    },
    {
      value: '$$$',
      label: 'Premium',
      icon: <FaWineBottle />,
      priceRange: { min: 4800, max: 9000 },
    },
    {
      value: '$$$$',
      label: 'Fine Dining',
      icon: <GiCrownedHeart />,
      priceRange: { min: 9000, max: 20000 },
    },
  ];

  // Update local price range when budget level changes
  useEffect(() => {
    if (formData.budgetLevel) {
      const selectedOption = budgetOptions.find(opt => opt.value === formData.budgetLevel);
      if (selectedOption) {
        setLocalPriceRange(selectedOption.priceRange);
      }
    }
  }, [formData.budgetLevel]);

  // Update form data when local price range changes
  useEffect(() => {
    updateFormData({
      priceRange: localPriceRange,
    //   averageBudget: Math.round((localPriceRange.min + localPriceRange.max) / 2)
    });
  }, [localPriceRange]);

  const handleBudgetSelect = (budgetLevel: string, priceRange: { min: number; max: number }) => {
    updateFormData({ budgetLevel });
    setLocalPriceRange(priceRange);
  };

  const handlePriceChange = (field: 'min' | 'max', value: string) => {
    const numValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
    setLocalPriceRange(prev => {
      const newRange = { ...prev, [field]: numValue };
      
      // Ensure min doesn't exceed max and vice versa
      if (field === 'min' && numValue > prev.max) {
        newRange.max = numValue;
      } else if (field === 'max' && numValue < prev.min) {
        newRange.min = numValue;
      }
      
      return newRange;
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'Ksh',
      minimumFractionDigits: 0,
    }).format(amount);
  };
  
  return (
    <div className="space-y-6">
      {/* Quick Selection Buttons - Single Row */}
      <div className={isActive ? 'fade-in' : 'opacity-0'}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {budgetOptions.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleBudgetSelect(option.value, option.priceRange)}
              className={`
                flex flex-col items-center p-3 rounded-lg border-2 transition-all duration-200
                ${formData.budgetLevel === option.value
                  ? 'border-[#E6B74C] bg-[#FFF8E7] shadow-sm'
                  : 'border-[#EDEDED] bg-white hover:border-[#FFD166]'
                }
                ${isActive ? 'fade-in stagger-' + (index + 1) : 'opacity-0'}
              `}
            >
              <span className="text-xl mb-1">{option.icon}</span>
              <span className="font-medium text-[#111827] text-sm text-center break-words">
                {option.label}
              </span>
              <span className="flex items-center text-[#FFD166] text-xs font-medium mt-1 flex-wrap justify-center gap-1">
                {formatCurrency(option.priceRange.min)} <FiMinus /> {formatCurrency(option.priceRange.max)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Price Range Inputs */}
      <div className={isActive ? 'fade-in stagger-5' : 'opacity-0'}>
        <div className="bg-white rounded-lg border border-[#FFDDA6] p-4">
          <h3 className="text-lg font-semibold text-[#111827] mb-3 text-center">
            Set Custom Price Range
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Min Price Input */}
            <div>
              <label className="block text-sm font-medium text-[#111827] mb-2">
                Starting Price
              </label>
              <div className="relative w-full border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#5E503F]">KSH:</span>
                <input
                  type="text"
                  value={localPriceRange.min.toLocaleString()}
                  onChange={(e) => handlePriceChange('min', e.target.value)}
                  className="w-full pl-12 pr-4 py-2"
                  placeholder="1,000"
                />
              </div>
            </div>

            {/* Max Price Input */}
            <div>
              <label className="block text-sm font-medium text-[#111827] mb-2">
                Maximum Price
              </label>
              <div className="relative w-full border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent">
                <span className="absolute left-3 mr-2 top-1/2 transform -translate-y-1/2 text-[#5E503F]">KSH:</span>
                <input
                  type="text"
                  value={localPriceRange.max.toLocaleString()}
                  onChange={(e) => handlePriceChange('max', e.target.value)}
                  className="w-full pl-12 pr-8 py-2 "
                  placeholder="20,000"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetStep;